// 1. React imports
import { useCallback, useEffect } from 'react';

// 2. External libraries
import { useClerk, useUser } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';

// 3. Internal components
import { OnboardingWizardTelegram } from '~/components/onboarding/OnboardingWizardTelegram';
import { TermsAcceptanceForm } from '~/components/auth/TermsAcceptanceForm';

/**
 * Onboarding page for new users.
 *
 * Conditional rendering logic (per spec):
 * - If onboardingComplete → Redirect to "/" (includes grandfather clause users)
 * - If !termsAccepted → Display TermsAcceptanceForm
 * - If termsAccepted && !onboardingComplete → Display OnboardingWizardTelegram
 *
 * Wizard resumes at currentStep if user returns mid-flow.
 *
 * Per §TS.3.1: Function declaration for named export
 * Per §TS.4.1: Imports organized by category with blank line separation
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { signOut } = useClerk();

  const termsAccepted = user?.publicMetadata?.termsAccepted;
  const onboardingComplete = user?.publicMetadata?.onboardingComplete;

  // All hooks must be called unconditionally before any early returns
  // Per React Rules of Hooks: Hooks must be called in the exact same order
  const handleComplete = useCallback(() => {
    void navigate({ to: '/' });
  }, [navigate]);

  const handleSignOut = useCallback(() => {
    void signOut();
  }, [signOut]);

  const handleAccept = useCallback(() => {
    void user?.reload();
  }, [user]);

  /**
   * Redirect effect for completed users.
   *
   * Per spec: "If onboardingComplete → Redirect to '/' (dashboard)"
   * Uses useEffect to maintain hook call order while still redirecting.
   */
  useEffect(() => {
    if (onboardingComplete === true) {
      void navigate({ to: '/', replace: true });
    }
  }, [onboardingComplete, navigate]);

  // Don't render anything while redirecting
  if (onboardingComplete === true) {
    return null;
  }

  // Show terms acceptance form if user has not accepted terms
  // Per spec: "If !termsAccepted → Display TermsAcceptanceForm"
  if (!termsAccepted) {
    return (
      <div className="mx-auto max-w-xl px-6">
        <div className="mt-16 mb-8">
          <h1 className="mb-4 font-heading text-4xl font-bold tracking-tight">
            Welcome to DeadDrop
          </h1>
          <TermsAcceptanceForm
            userEmail={user?.emailAddresses[0]?.emailAddress ?? ''}
            onAccept={handleAccept}
            onSignOut={handleSignOut}
          />
        </div>
      </div>
    );
  }

  // Show onboarding wizard if terms accepted but onboarding not complete
  // Per spec: "If termsAccepted && !onboardingComplete → Display OnboardingWizardTelegram"
  return <OnboardingWizardTelegram onComplete={handleComplete} />;
}
